import { computed, ref, Ref, nextTick } from 'vue';
import { ActDateTypes, ActDateType, IDashboardAccessPeriodsValidationIssue } from '@/models/act/act-access.interface';
import { ProjectProduct } from '@studyportals/crm-data-processor-interface';
import datePresenter from '@/presenters/date-presenter';
import actPresenter from '@/presenters/act-presenter';
import actStore from '@/store/act';

export default class ActSetAccessPeriodsCode {
	public datePickerStart: Ref<Record<number, HTMLInputElement>> = ref({});
	public datePickerEnd: Ref<Record<number, HTMLInputElement>> = ref({});

	public get dateTypeStart(): ActDateType {
		return ActDateTypes.START;
	}

	public get dateTypeEnd(): ActDateType {
		return ActDateTypes.END;
	}

	public dashboardAccessPeriodsValidationIssues = computed((): IDashboardAccessPeriodsValidationIssue[] => {
		return actPresenter.dashboardAccessPeriodsValidationIssues.value;
	});

	public sameDatesPerDashboard = computed((): boolean => {
		return actStore.actGetters.actSameDateForDashboards();
	});

	public actDashboardsToShowDatesFor = computed((): ProjectProduct[] => {
		if (this.sameDatesPerDashboard.value) {
			return [this.actDashboardsToAdd.value[0]];
		}

		return this.actDashboardsToAdd.value;
	});

	private actDashboardsToAdd = computed((): ProjectProduct[] => {
		return actStore.actGetters.actDashboardsToAdd();
	});

	public setDatePickerRef(refs: Record<number, HTMLInputElement>, index: number): any {
		return (element: Element | null) => {
			if (!element) {
				return;
			}

			refs[index] = element as HTMLInputElement;
		};
	}

	public retrieveAccessPeriodsValidationIssueForDashboard(
		dashboardId: string,
		type?: ActDateType
	): IDashboardAccessPeriodsValidationIssue | undefined {
		return this.dashboardAccessPeriodsValidationIssues.value.find(
			issue => issue.productId === dashboardId && (!type || issue.field === type)
		);
	}

	public handleCheckboxClick(): void {
		actStore.actMutations.toggleActSameDateForDashboards();
	}

	public async prefillDatePickers(): Promise<void> {
		await nextTick();

		this.actDashboardsToShowDatesFor.value.forEach((dashboard, index) => {
			const adjustedIndex = this.sameDatesPerDashboard.value ? 0 : index;
			const startPicker = this.datePickerStart.value[adjustedIndex];
			const endPicker = this.datePickerEnd.value[adjustedIndex];

			if (startPicker && dashboard.productStartDate) {
				startPicker.value = datePresenter.transformDateObjectToUTCDateString(dashboard.productStartDate, true);
			}

			if (endPicker && dashboard.productEndDate) {
				endPicker.value = datePresenter.transformDateObjectToUTCDateString(dashboard.productEndDate, true);
			}
		});
	}

	public handleDateSet(index: number, event: Event, type: ActDateType): void {
		if (this.sameDatesPerDashboard.value) {
			this.actDashboardsToAdd.value.forEach((dashboard, loopIndex) => {
				const dateForAllDashboards = this.extractDateFromEvent(event);
				this.setActDashboardDate(loopIndex, dateForAllDashboards, type);
			});
			return;
		}

		const dateForOneDashboard = this.extractDateFromEvent(event);
		this.setActDashboardDate(index, dateForOneDashboard, type);
	}

	private extractDateFromEvent(event: Event): Date | null {
		const target = event.target as unknown as { value: string | null };
		if (!target?.value) {
			return null;
		}

		return datePresenter.transformUTCDateStringToDateObject(target.value);
	}

	private setActDashboardDate(index: number, date: Date | null, dateType: ActDateType): void {
		if (dateType === ActDateTypes.START) {
			actStore.actMutations.setActDashboardStartDate(index, date);
		} else {
			actStore.actMutations.setActDashboardEndDate(index, date);
		}
	}
}
